// src/app/services/publishing-api.ts
import { apiFetch } from "@/api/api-fetch";

// ─── Types ────────────────────────────────────────────────────────────────────

export type Destination = "hashnode" | "wordpress" | "linkedin";

export const DESTINATIONS: Destination[] = ["hashnode", "wordpress", "linkedin"];

export interface HashnodeCredentials {
  personal_access_token: string;
  publication_host?: string | null;
}

export interface WordPressCredentials {
  site_url: string;
  username: string;
  application_password: string;
}

export interface LinkedInCredentials {
  access_token: string;
  author_urn: string;
  expires_at?: string | null;
}

export interface AccountMetadata {
  display_name?: string;
  username?: string;
  user_id?: string | number;
  site_url?: string;
  author_urn?: string;
  type?: "person" | "organization";
  image_url?: string;
  profile_url?: string;
  publication_id?: string;
  publication_title?: string;
  publication_url?: string;
  publication_host?: string;
  publication_not_found?: string;
  note?: string;
  [k: string]: any;
}

export interface Integration {
  id: number;
  destination: Destination;
  brand_name: string;
  account_metadata: AccountMetadata;
  status: "active" | "expired" | "revoked" | "error";
  expires_at: string | null;
  last_verified_at: string;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface TestResult {
  ok: boolean;
  account_metadata: AccountMetadata;
  error?: string | null;
}

export interface ConnectResult {
  ok: boolean;
  id?: number;
  account_metadata?: AccountMetadata;
  expires_at?: string | null;
}

export interface PolicyWarning {
  destination: string;
  field: string;
  severity: "hard" | "soft";
  message: string;
}

export interface ContextRef {
  type: "fix_action" | "brand_insight" | "brand_run" | "cognition_run" | "sandbox_run";
  id: number;
  label: string;
}

// Per-destination content shapes
export interface HashnodeContent {
  title?: string;
  content_markdown?: string;
  tags?: string[];
  slug?: string;
  original_article_url?: string;
}

export interface WordPressContent {
  title?: string;
  content_html?: string;
  excerpt?: string;
  status?: "draft" | "publish" | "pending" | "private" | "future";
}

export interface LinkedInContent {
  text?: string;
  visibility?: "PUBLIC" | "CONNECTIONS";
}

export type PerDestinationContent = {
  hashnode?: HashnodeContent;
  wordpress?: WordPressContent;
  linkedin?: LinkedInContent;
};

export interface Draft {
  id: number;
  username: string;
  brand_name: string;
  brief: string;
  source_ref: Record<string, any>;
  destinations: Destination[];
  per_destination_content: PerDestinationContent;
  status: "pending" | "approved" | "partially_published" | "published" | "failed" | "cancelled";
  policy_warnings: PolicyWarning[];
  input_tokens: number;
  output_tokens: number;
  model_used: string;
  created_at: string;
  updated_at: string;
}

export interface PublishResultItem {
  destination: Destination;
  status: "published" | "failed";
  external_id: string | null;
  external_url: string | null;
  error?: string | null;
  history_id: number | null;
}

export interface PublishResponse {
  draft_id: number;
  overall_status: "published" | "partially_published" | "failed";
  results: PublishResultItem[];
}

export interface HistoryItem {
  id: number;
  draft_id: number | null;
  destination: Destination;
  brand_name: string;
  content_snapshot: Record<string, any>;
  external_id: string | null;
  external_url: string | null;
  status: string;
  error_message: string | null;
  published_at: string;
}

export interface PublishGate {
  linked: boolean;
  content_draft: { customer_id: number; draft_id: number; version_number: number } | null;
  destinations: Partial<Record<Destination, { live: boolean; ready: boolean; blockers: { code: string; message: string }[] }>>;
}

// ─── API calls ────────────────────────────────────────────────────────────────

export const publishingApi = {
  // Integrations
  listIntegrations: (brandName?: string) => {
    const qs = brandName ? `?brand_name=${encodeURIComponent(brandName)}` : "";
    return apiFetch<{ items: Integration[] }>(`/publishing/integrations${qs}`).then(r => r.items);
  },

  testConnection: (body: {
    destination: Destination;
    hashnode?: HashnodeCredentials;
    wordpress?: WordPressCredentials;
    linkedin?: LinkedInCredentials;
  }) =>
    apiFetch<TestResult>("/publishing/integrations/test", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  connect: (body: {
    destination: Destination;
    brand_name?: string;
    hashnode?: HashnodeCredentials;
    wordpress?: WordPressCredentials;
    linkedin?: LinkedInCredentials;
  }) =>
    apiFetch<ConnectResult>("/publishing/integrations/connect", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  disconnect: (destination: Destination, brandName?: string) => {
    const qs = brandName ? `?brand_name=${encodeURIComponent(brandName)}` : "";
    return apiFetch<{ ok: boolean }>(`/publishing/integrations/${destination}${qs}`, {
      method: "DELETE",
    });
  },

  // Drafts
  listDrafts: (brandName?: string, limit = 50) => {
    const params = new URLSearchParams();
    if (brandName) params.set("brand_name", brandName);
    params.set("limit", String(limit));
    return apiFetch<{ items: Draft[] }>(`/publishing/drafts?${params.toString()}`).then(r => r.items);
  },

  createDraft: (body: {
    brief: string;
    destinations: Destination[];
    brand_name?: string;
    source_ref?: ContextRef;
    tone?: string;
    /** #132: the content brief to write from (POST /content/customers/{id}/briefs). */
    brief_id?: number;
  }) =>
    apiFetch<Draft>("/publishing/drafts", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createManualDraft: (body: {
    destinations: Destination[];
    per_destination_content: PerDestinationContent;
    brand_name?: string;
    title_hint?: string;
    source_ref?: ContextRef;
  }) =>
    apiFetch<Draft>("/publishing/drafts/manual", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getDraft: (id: number) => apiFetch<Draft>(`/publishing/drafts/${id}`),

  /** #132: confirm a blocking claim as a fact, or remove its sentence. */
  resolveClaim: (id: number, body: {
    destination: Destination; sentence_index: number; resolution: "confirm_fact" | "remove"; note?: string;
  }) =>
    apiFetch<Draft>(`/publishing/drafts/${id}/claims/resolve`, { method: "POST", body: JSON.stringify(body) }),

  /** #132: a fresh model claim check of the draft's current text. */
  recheckClaims: (id: number) =>
    apiFetch<Draft>(`/publishing/drafts/${id}/claims/recheck`, { method: "POST" }),

  updateDraft: (id: number, per_destination_content: PerDestinationContent) =>
    apiFetch<{ ok: boolean; policy_warnings: PolicyWarning[] }>(`/publishing/drafts/${id}`, {
      method: "PUT",
      body: JSON.stringify({ per_destination_content }),
    }),

  deleteDraft: (id: number) =>
    apiFetch<{ ok: boolean }>(`/publishing/drafts/${id}`, { method: "DELETE" }),

  // Publish
  publish: (draftId: number, body: {
    destinations: Destination[];
    per_destination_content?: PerDestinationContent;
  }) =>
    apiFetch<PublishResponse>(`/publishing/drafts/${draftId}/publish`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** #133: per destination, whether it goes live and what still blocks it
   *  (a tested, approved content-studio version, unchanged since it was sent). */
  getGate: (draftId: number) =>
    apiFetch<PublishGate>(`/publishing/drafts/${draftId}/gate`),

  /** #133: bring this draft's text into the content studio, to test and
   *  approve it there (live publishing needs that). */
  toContentStudio: (draftId: number) =>
    apiFetch<{ customer_id: number; draft_id: number }>(`/publishing/drafts/${draftId}/to-content-studio`, {
      method: "POST",
    }),

  // History
  listHistory: (opts?: { brand_name?: string; destination?: Destination; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.brand_name) params.set("brand_name", opts.brand_name);
    if (opts?.destination) params.set("destination", opts.destination);
    params.set("limit", String(opts?.limit ?? 50));
    return apiFetch<{ items: HistoryItem[] }>(`/publishing/history?${params.toString()}`).then(r => r.items);
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function destinationLabel(d: Destination): string {
  return { hashnode: "Hashnode", wordpress: "WordPress", linkedin: "LinkedIn" }[d];
}

export function destinationColor(d: Destination): string {
  return {
    hashnode: "#2962FF",
    wordpress: "#21759B",
    linkedin: "#0A66C2",
  }[d];
}

export function daysUntilExpiry(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const ms = then - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}